import { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import Cache from '@fs/zion-cache'
import { useSyncToStorage } from '@fs/zion-storage-sync'

const storeName = 'syncStorage'

/**
 * useSyncedValue is a hook that syncs a value to a cache with the specific databaseKey
 * @param databaseKey The key to store the value in the cache.
 * @param setValue The function that runs to set the value from cache.
 * @param value The value to sync to cache. Do not set the initial state of the value to '', use null instead.
 */
export default function useSyncedValue({ databaseKey, value, setValue }) {
  const cache = useMemo(() => {
    return new Cache({ storeName })
  }, [])

  useSyncToStorage({
    value,
    key: databaseKey,
    cache,
  })

  useEffect(() => {
    let isCurrent = true
    if (databaseKey) {
      cache
        .getItem(databaseKey)
        .then((storedValue) => {
          if (storedValue) {
            isCurrent && setValue(storedValue)
          }
          return null
        })
        .catch(console.error)
    }
    return () => {
      isCurrent = false
    }
  }, [cache, databaseKey, setValue])
}

useSyncedValue.propTypes = {
  databaseKey: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array, PropTypes.bool]).isRequired,
  setValue: PropTypes.func.isRequired,
}
